import { useState, useCallback } from 'react';
import { AxiosError } from 'axios';
import {
  checkCostDuplicates,
  checkChargeDuplicates,
  checkPaymentDuplicates,
  type CostDuplicateCheckRequest,
  type ChargeDuplicateCheckRequest,
  type PaymentDuplicateCheckRequest,
  type JobEntryDuplicateResult,
} from '../api/checkJobEntryDuplicates';

interface UseJobEntryDuplicateCheckReturn {
  checkForCostDuplicates: (
    jobId: number,
    data: CostDuplicateCheckRequest
  ) => Promise<JobEntryDuplicateResult>;
  checkForChargeDuplicates: (
    jobId: number,
    data: ChargeDuplicateCheckRequest
  ) => Promise<JobEntryDuplicateResult>;
  checkForPaymentDuplicates: (
    jobId: number,
    data: PaymentDuplicateCheckRequest
  ) => Promise<JobEntryDuplicateResult>;
  isChecking: boolean;
  error: string | null;
  clearError: () => void;
}

/**
 * Hook for checking potential duplicate job entries (costs, charges, payments) before saving.
 *
 * @example
 * const { checkForCostDuplicates, isChecking } = useJobEntryDuplicateCheck();
 *
 * const result = await checkForCostDuplicates(jobId, {
 *   amount: 500.00,
 *   paidTo: 'Home Depot',
 *   paymentDate: '2025-01-15',
 *   excludeCostId: costId, // when editing
 * });
 *
 * if (result.hasDuplicates) {
 *   // Show warning dialog
 * }
 */
export function useJobEntryDuplicateCheck(): UseJobEntryDuplicateCheckReturn {
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkForCostDuplicates = useCallback(
    async (
      jobId: number,
      data: CostDuplicateCheckRequest
    ): Promise<JobEntryDuplicateResult> => {
      setIsChecking(true);
      setError(null);

      try {
        const result = await checkCostDuplicates(jobId, data);
        return result;
      } catch (err) {
        const axiosError = err as AxiosError<{ message?: string }>;
        const errorMessage =
          axiosError.response?.data?.message || 'Failed to check for duplicate costs';
        setError(errorMessage);
        // Return no duplicates on error to allow form submission to proceed
        return { hasDuplicates: false, duplicates: [] };
      } finally {
        setIsChecking(false);
      }
    },
    []
  );

  const checkForChargeDuplicates = useCallback(
    async (
      jobId: number,
      data: ChargeDuplicateCheckRequest
    ): Promise<JobEntryDuplicateResult> => {
      setIsChecking(true);
      setError(null);

      try {
        const result = await checkChargeDuplicates(jobId, data);
        return result;
      } catch (err) {
        const axiosError = err as AxiosError<{ message?: string }>;
        const errorMessage =
          axiosError.response?.data?.message || 'Failed to check for duplicate charges';
        setError(errorMessage);
        // Return no duplicates on error to allow form submission to proceed
        return { hasDuplicates: false, duplicates: [] };
      } finally {
        setIsChecking(false);
      }
    },
    []
  );

  const checkForPaymentDuplicates = useCallback(
    async (
      jobId: number,
      data: PaymentDuplicateCheckRequest
    ): Promise<JobEntryDuplicateResult> => {
      setIsChecking(true);
      setError(null);

      try {
        const result = await checkPaymentDuplicates(jobId, data);
        return result;
      } catch (err) {
        const axiosError = err as AxiosError<{ message?: string }>;
        const errorMessage =
          axiosError.response?.data?.message || 'Failed to check for duplicate payments';
        setError(errorMessage);
        // Return no duplicates on error to allow form submission to proceed
        return { hasDuplicates: false, duplicates: [] };
      } finally {
        setIsChecking(false);
      }
    },
    []
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    checkForCostDuplicates,
    checkForChargeDuplicates,
    checkForPaymentDuplicates,
    isChecking,
    error,
    clearError,
  };
}
